/* eslint-disable import/no-unresolved, no-underscore-dangle */
/* global window */
import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import ReactPaginate from 'react-paginate';
import { Ring2 } from 'ldrs/react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import Author from './Author';
import Back from './Back';
import Featured from './Featured';
import Filters from './Filters';
import Post from './Post';
import 'ldrs/react/Ring2.css';
import '../css/cf-post-listing.scss';

const Posts = ({ path }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { page } = useParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featured, setFeatured] = useState({});
  const [pages, setPages] = useState(0);
  const [categories, setCategories] = useState([]);
  const category = searchParams.get('category');
  const author = searchParams.get('by');

  useEffect(() => {
    const el = document.querySelector('#cf-post-listing');
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Only scroll if the element's top is ABOVE the viewport
    if (rect.top < 0) {
      const absoluteTop = rect.top + window.pageYOffset;
      window.scrollTo({
        top: absoluteTop,
        behavior: 'instant',
      });
    }
  }, [location]);

  useEffect(() => {
    async function fetchPosts() {
      setLoading(true);
      try {
        let fp;
        // Shared params between queries
        const p = {
          _embed: true,
          orderby: 'date',
          order: 'desc',
        };
        // Optional category filter
        if (searchParams.get('category') && !searchParams.get('by')) {
          p.categories = Number(searchParams.get('category'));
        }
        // Optional author filter
        if (searchParams.get('by')) {
          p.acf_author = Number(searchParams.get('by'));
        }
        //
        // 1. Get featured post (sticky → fallback to latest)
        //
        const fresponse = await axios.get('/wp-json/wp/v2/posts', {
          params: {
            ...p,
            per_page: 1,
            sticky: true,
          },
        });

        if (fresponse.data.length) {
          const [f] = fresponse.data;
          setFeatured(f);
          fp = f;
        } else {
          const lresponse = await axios.get('/wp-json/wp/v2/posts', {
            params: {
              ...p,
              per_page: 1,
            },
          });
          const [l] = lresponse.data;
          setFeatured(l);
          fp = l;
        }
        //
        // 2. Fetch remaining posts
        //
        const response = await axios.get('/wp-json/wp/v2/posts', {
          params: {
            ...p,
            exclude: fp?.id,
            page,
            per_page: 6,
          },
        });
        setPages(parseInt(response?.headers?.['x-wp-totalpages'], 10));
        setPosts(response.data);
        //
        // 3. Fetch categories for filter dropdown
        //
        const cresponse = await axios.get('/wp-json/wp/v2/categories', {
          params: {
            per_page: 100,
            hide_empty: true,
          },
        });
        setCategories(cresponse.data);
      } catch (err) {
        navigate(`${path}page/1`);
      } finally {
        setLoading(false);
      }
    }
    fetchPosts();
  }, [page, category, author]);

  if (loading) {
    return (
      <div className="loader-wrapper">
        <Ring2
          size="75"
          stroke="8"
          strokeLength="0.25"
          bgOpacity="0.1"
          speed="0.9"
          color="#000d2b"
        />
      </div>
    );
  }

  let filteredAuthor;
  if (searchParams.get('by')) {
    const authors = featured?.authors_expanded || posts[0]?.authors_expanded;
    const targetId = Number(searchParams.get('by'));
    filteredAuthor = authors.find(a => a.id === targetId);
  }

  return (
    <div>
      {searchParams.get('by') ? (
        <>
          <Back path={path} />
          <Author author={filteredAuthor} />
        </>
      ) : (
        <Filters categories={categories} path={path} />
      )}
      {(page === '1' && featured) && (
        <Featured post={featured} path={path} hasGrid={posts && !!posts.length} />
      )}
      {posts && !!posts.length && (
        <div className="posts-grid">
          {posts.map((p) => <Post key={p.id} post={p} path={path} />)}
        </div>
      )}
      {(!(page === '1' && featured) && !(posts && !!posts.length)) && (
        <h2>No results found.</h2>
      )}
      {Number(pages) > 1 && (
        <div>
          <ReactPaginate
            activeClassName="is-active"
            breakLabel="..."
            className="pagination"
            forcePage={Number(page) - 1}
            nextLabel=">"
            onPageChange={(event) => {
              const { search } = location;
              navigate(`${path}page/${event.selected + 1}${search}`);
            }}
            pageCount={pages}
            pageLinkClassName="pagination-link"
            previousLabel="<"
            renderOnZeroPageCount={null}
          />
        </div>
      )}
    </div>
  );
};

Posts.propTypes = {
  path: PropTypes.string.isRequired,
};

export default Posts;
